<template>
  <el-dialog
    :visible.sync="visible"
    width="780px"
    :show-close="false"
    :close-on-click-modal="false"
    append-to-body
    custom-class="auth-dialog-modern"
    @close="handleClose"
  >
    <button class="dialog-close" type="button" @click="visible = false">
      <i class="el-icon-close"></i>
    </button>

    <div class="auth-layout">
      <!-- 左侧品牌区 -->
      <div class="auth-brand">
        <div class="brand-bg">
          <div class="bg-circle c1"></div>
          <div class="bg-circle c2"></div>
          <div class="bg-circle c3"></div>
        </div>
        <div class="brand-content">
          <div class="brand-logo">
            <i class="el-icon-edit-outline"></i>
          </div>
          <h2>{{ brandTitle }}</h2>
          <p>{{ brandDesc }}</p>
          <ul v-if="activeTab !== 'forgot'" class="brand-features">
            <li><i class="el-icon-check"></i> 收藏喜欢的文章</li>
            <li><i class="el-icon-check"></i> 同步浏览历史记录</li>
            <li><i class="el-icon-check"></i> 个性化阅读体验</li>
          </ul>
          <ul v-else class="brand-features">
            <li><i class="el-icon-check"></i> 邮箱验证保障账号安全</li>
            <li><i class="el-icon-check"></i> 验证码 10 分钟内有效</li>
            <li><i class="el-icon-check"></i> 重置后可立即登录</li>
          </ul>
        </div>
      </div>

      <!-- 右侧表单区 -->
      <div class="auth-form-panel">
        <div class="form-header">
          <div v-if="activeTab !== 'forgot'" class="tab-switch">
            <button
              type="button"
              class="tab-btn"
              :class="{ active: activeTab === 'login' }"
              @click="activeTab = 'login'"
            >
              登录
            </button>
            <button
              type="button"
              class="tab-btn"
              :class="{ active: activeTab === 'register' }"
              @click="activeTab = 'register'"
            >
              注册
            </button>
            <span class="tab-indicator" :class="activeTab"></span>
          </div>
          <div v-else class="forgot-header">
            <button type="button" class="back-login-btn" @click="goBackLogin">
              <i class="el-icon-arrow-left"></i>
              返回登录
            </button>
            <h3>找回密码</h3>
            <p>{{ resetStep === 1 ? '输入注册邮箱，获取验证码' : '设置您的新密码' }}</p>
          </div>
        </div>

        <transition name="form-slide" mode="out-in">
          <!-- 登录表单 -->
          <div v-if="activeTab === 'login'" key="login" class="form-body">
            <div class="login-mode-switch">
              <button
                type="button"
                class="mode-btn"
                :class="{ active: loginMode === 'password' }"
                @click="switchLoginMode('password')"
              >
                账号登录
              </button>
              <button
                type="button"
                class="mode-btn"
                :class="{ active: loginMode === 'qr' }"
                @click="switchLoginMode('qr')"
              >
                小程序扫码
              </button>
            </div>

            <div v-if="loginMode === 'qr'" class="qr-login-panel">
              <div class="qr-box" v-loading="qrLoading">
                <img v-if="qrImage" :src="qrImage" alt="扫码登录" class="qr-image" />
                <div v-else class="qr-empty">
                  <i class="el-icon-full-screen"></i>
                  <p>{{ qrHint || '二维码加载中…' }}</p>
                </div>
                <div v-if="qrStatus === 'expired'" class="qr-mask" @click="refreshQrLogin">
                  <span>已过期，点击刷新</span>
                </div>
              </div>
              <p class="qr-status-text">{{ qrStatusText }}</p>
              <el-button type="text" icon="el-icon-refresh" @click="refreshQrLogin">刷新二维码</el-button>
            </div>

            <el-form
              v-else
              ref="loginForm"
              :model="loginForm"
              :rules="loginRules"
              label-width="0"
              @submit.native.prevent
            >
              <el-form-item prop="account">
                <label class="field-label">账号</label>
                <el-input
                  v-model="loginForm.account"
                  prefix-icon="el-icon-user"
                  placeholder="用户名或邮箱"
                  size="medium"
                  @keyup.enter.native="handleLogin"
                />
              </el-form-item>
              <el-form-item prop="password">
                <div class="field-label-row">
                  <label class="field-label">密码</label>
                  <a href="#" class="forgot-link" @click.prevent="goForgot">忘记密码？</a>
                </div>
                <el-input
                  v-model="loginForm.password"
                  type="password"
                  prefix-icon="el-icon-lock"
                  placeholder="请输入密码"
                  size="medium"
                  show-password
                  @keyup.enter.native="handleLogin"
                />
              </el-form-item>
              <el-button
                type="primary"
                class="submit-btn"
                :loading="loading"
                @click="handleLogin"
              >
                登录
                <i class="el-icon-right"></i>
              </el-button>
            </el-form>
            <p class="form-footer">
              还没有账号？
              <a href="#" @click.prevent="activeTab = 'register'">立即注册</a>
            </p>
          </div>

          <!-- 注册表单 -->
          <div v-else-if="activeTab === 'register'" key="register" class="form-body">
            <el-form
              ref="registerForm"
              :model="registerForm"
              :rules="registerRules"
              label-width="0"
              @submit.native.prevent
            >
              <el-form-item prop="username">
                <label class="field-label">用户名</label>
                <el-input
                  v-model="registerForm.username"
                  prefix-icon="el-icon-user"
                  placeholder="须大于 5 位"
                  size="medium"
                />
              </el-form-item>
              <el-form-item prop="email">
                <label class="field-label">邮箱</label>
                <el-input
                  v-model="registerForm.email"
                  prefix-icon="el-icon-message"
                  placeholder="your@email.com"
                  size="medium"
                />
              </el-form-item>
              <el-form-item prop="code">
                <label class="field-label">邮箱验证码</label>
                <div class="code-input-row">
                  <el-input
                    v-model="registerForm.code"
                    prefix-icon="el-icon-key"
                    placeholder="6 位验证码"
                    size="medium"
                    maxlength="6"
                  />
                  <el-button
                    class="code-btn"
                    :disabled="registerCodeCountdown > 0 || loading"
                    @click="handleSendRegisterCode"
                  >
                    {{ registerCodeCountdown > 0 ? `${registerCodeCountdown}s 后重发` : '获取验证码' }}
                  </el-button>
                </div>
              </el-form-item>
              <el-form-item prop="password">
                <label class="field-label">密码</label>
                <el-input
                  v-model="registerForm.password"
                  type="password"
                  prefix-icon="el-icon-lock"
                  placeholder="须大于 8 位"
                  size="medium"
                  show-password
                />
              </el-form-item>
              <el-form-item prop="confirmPassword">
                <label class="field-label">确认密码</label>
                <el-input
                  v-model="registerForm.confirmPassword"
                  type="password"
                  prefix-icon="el-icon-lock"
                  placeholder="再次输入密码"
                  size="medium"
                  show-password
                  @keyup.enter.native="handleRegister"
                />
              </el-form-item>
              <el-button
                type="primary"
                class="submit-btn"
                :loading="loading"
                @click="handleRegister"
              >
                创建账号
                <i class="el-icon-right"></i>
              </el-button>
            </el-form>
            <p class="form-footer">
              已有账号？
              <a href="#" @click.prevent="activeTab = 'login'">立即登录</a>
            </p>
          </div>

          <!-- 找回密码 -->
          <div v-else key="forgot" class="form-body">
            <div class="reset-steps">
              <span class="step" :class="{ active: resetStep >= 1, done: resetStep > 1 }">1</span>
              <span class="step-line" :class="{ active: resetStep > 1 }"></span>
              <span class="step" :class="{ active: resetStep >= 2 }">2</span>
            </div>

            <el-form
              v-if="resetStep === 1"
              ref="forgotEmailForm"
              :model="forgotForm"
              :rules="forgotEmailRules"
              label-width="0"
              @submit.native.prevent
            >
              <el-form-item prop="email">
                <label class="field-label">注册邮箱</label>
                <el-input
                  v-model="forgotForm.email"
                  prefix-icon="el-icon-message"
                  placeholder="请输入注册时使用的邮箱"
                  size="medium"
                  @keyup.enter.native="handleSendCode"
                />
              </el-form-item>
              <el-button
                type="primary"
                class="submit-btn"
                :loading="loading"
                @click="handleSendCode"
              >
                发送验证码
                <i class="el-icon-right"></i>
              </el-button>
            </el-form>

            <el-form
              v-else
              ref="forgotResetForm"
              :model="forgotForm"
              :rules="forgotResetRules"
              label-width="0"
              @submit.native.prevent
            >
              <el-form-item>
                <label class="field-label">注册邮箱</label>
                <el-input
                  :value="forgotForm.email"
                  prefix-icon="el-icon-message"
                  size="medium"
                  disabled
                />
              </el-form-item>
              <el-form-item prop="code">
                <label class="field-label">验证码</label>
                <div class="code-input-row">
                  <el-input
                    v-model="forgotForm.code"
                    prefix-icon="el-icon-key"
                    placeholder="6 位验证码"
                    size="medium"
                    maxlength="6"
                    @keyup.enter.native="handleResetPassword"
                  />
                  <el-button
                    class="code-btn"
                    :disabled="codeCountdown > 0 || loading"
                    @click="handleSendCode"
                  >
                    {{ codeCountdown > 0 ? `${codeCountdown}s 后重发` : '重新发送' }}
                  </el-button>
                </div>
              </el-form-item>
              <el-form-item prop="password">
                <label class="field-label">新密码</label>
                <el-input
                  v-model="forgotForm.password"
                  type="password"
                  prefix-icon="el-icon-lock"
                  placeholder="至少 6 位"
                  size="medium"
                  show-password
                />
              </el-form-item>
              <el-form-item prop="confirmPassword">
                <label class="field-label">确认新密码</label>
                <el-input
                  v-model="forgotForm.confirmPassword"
                  type="password"
                  prefix-icon="el-icon-lock"
                  placeholder="再次输入新密码"
                  size="medium"
                  show-password
                  @keyup.enter.native="handleResetPassword"
                />
              </el-form-item>
              <el-button
                type="primary"
                class="submit-btn"
                :loading="loading"
                @click="handleResetPassword"
              >
                重置密码
                <i class="el-icon-right"></i>
              </el-button>
            </el-form>

            <p class="form-footer">
              想起密码了？
              <a href="#" @click.prevent="goBackLogin">返回登录</a>
            </p>
          </div>
        </transition>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import { auth } from '@/store/auth'

export default {
  name: 'AuthDialog',
  data() {
    const validateRegisterUsername = (rule, value, callback) => {
      if (!value || value.length <= 5) {
        callback(new Error('用户名须大于 5 位'))
      } else if (value.length > 20) {
        callback(new Error('用户名不能超过 20 位'))
      } else {
        callback()
      }
    }

    const validateRegisterPassword = (rule, value, callback) => {
      if (!value || value.length <= 8) {
        callback(new Error('密码须大于 8 位'))
      } else {
        callback()
      }
    }

    const validateConfirm = (rule, value, callback) => {
      if (value !== this.registerForm.password) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }

    const validateForgotConfirm = (rule, value, callback) => {
      if (value !== this.forgotForm.password) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }

    return {
      loading: false,
      resetStep: 1,
      codeCountdown: 0,
      registerCodeCountdown: 0,
      codeTimer: null,
      registerCodeTimer: null,
      loginMode: 'password',
      qrLoading: false,
      qrTicket: '',
      qrImage: '',
      qrHint: '',
      qrStatus: 'idle',
      qrExpireAt: '',
      qrPollTimer: null,
      loginForm: { account: '', password: '' },
      registerForm: {
        username: '',
        email: '',
        code: '',
        password: '',
        confirmPassword: ''
      },
      forgotForm: {
        email: '',
        code: '',
        password: '',
        confirmPassword: ''
      },
      loginRules: {
        account: [{ required: true, message: '请输入用户名或邮箱', trigger: 'blur' }],
        password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
      },
      registerRules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { validator: validateRegisterUsername, trigger: 'blur' }
        ],
        email: [
          { required: true, message: '请输入邮箱', trigger: 'blur' },
          { type: 'email', message: '邮箱格式不正确', trigger: 'blur' }
        ],
        code: [
          { required: true, message: '请输入邮箱验证码', trigger: 'blur' },
          { len: 6, message: '验证码为 6 位数字', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入密码', trigger: 'blur' },
          { validator: validateRegisterPassword, trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请确认密码', trigger: 'blur' },
          { validator: validateConfirm, trigger: 'blur' }
        ]
      },
      forgotEmailRules: {
        email: [
          { required: true, message: '请输入注册邮箱', trigger: 'blur' },
          { type: 'email', message: '邮箱格式不正确', trigger: 'blur' }
        ]
      },
      forgotResetRules: {
        code: [
          { required: true, message: '请输入验证码', trigger: 'blur' },
          { len: 6, message: '验证码为 6 位数字', trigger: 'blur' }
        ],
        password: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码至少 6 位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请确认新密码', trigger: 'blur' },
          { validator: validateForgotConfirm, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    qrStatusText() {
      if (this.qrStatus === 'pending') return '请使用微信扫一扫打开小程序并确认登录'
      if (this.qrStatus === 'expired') return '二维码已过期，请刷新'
      if (this.qrStatus === 'cancelled') return '已取消，可刷新后重试'
      if (this.qrStatus === 'error') return this.qrHint || '二维码加载失败'
      return '点击「小程序扫码」开始'
    },
    brandTitle() {
      if (this.activeTab === 'forgot') return '找回密码'
      if (this.activeTab === 'register') return '加入我们'
      return '欢迎回来'
    },
    brandDesc() {
      if (this.activeTab === 'forgot') {
        return this.resetStep === 1
          ? '通过注册邮箱验证身份，安全重置您的密码'
          : '设置新密码后即可重新登录账号'
      }
      if (this.activeTab === 'register') return '创建账号，探索更多优质内容'
      if (this.loginMode === 'qr') return '微信扫码，安全快捷登录网站'
      return '登录账号，继续你的阅读之旅'
    },
    visible: {
      get() {
        return auth.authDialogVisible
      },
      set(val) {
        if (!val) auth.closeAuthDialog()
      }
    },
    activeTab: {
      get() {
        return auth.authDialogTab
      },
      set(val) {
        auth.authDialogTab = val
      }
    }
  },
  watch: {
    visible(val) {
      if (val && this.activeTab === 'login' && this.loginMode === 'qr') {
        this.refreshQrLogin()
      }
      if (!val) this.stopQrPolling(true)
    },
    activeTab(val) {
      if (val !== 'login') {
        this.stopQrPolling(true)
        this.loginMode = 'password'
      } else if (this.loginMode === 'qr' && this.visible) {
        this.refreshQrLogin()
      }
    }
  },
  beforeDestroy() {
    this.clearCodeTimer()
    this.clearRegisterCodeTimer()
    this.stopQrPolling(true)
  },
  methods: {
    switchLoginMode(mode) {
      this.loginMode = mode
      if (mode === 'qr') this.refreshQrLogin()
      else this.stopQrPolling(true)
    },
    async refreshQrLogin() {
      this.stopQrPolling(false)
      this.qrLoading = true
      this.qrStatus = 'pending'
      this.qrImage = ''
      this.qrHint = ''
      this.qrTicket = ''
      const result = await auth.createQrLogin()
      this.qrLoading = false
      if (!result.success) {
        this.qrStatus = 'error'
        this.qrHint = result.message || '创建二维码失败'
        return
      }
      const data = result.data || {}
      this.qrTicket = data.ticket || ''
      this.qrImage = data.qrImage || ''
      this.qrHint = data.qrHint || ''
      this.qrExpireAt = data.expireAt || ''
      if (!this.qrImage) {
        this.qrStatus = 'error'
        return
      }
      this.startQrPolling()
    },
    startQrPolling() {
      this.stopQrPolling(false)
      this.qrPollTimer = setInterval(() => {
        this.pollQrOnce()
      }, 2000)
    },
    async pollQrOnce() {
      if (!this.qrTicket) return
      const result = await auth.pollQrLogin(this.qrTicket)
      if (!result.success) return
      const data = result.data || {}
      if (data.status === 'pending') {
        this.qrStatus = 'pending'
        return
      }
      if (data.status === 'expired' || data.status === 'cancelled') {
        this.qrStatus = data.status
        this.stopQrPolling(false)
        return
      }
      if (data.status === 'confirmed' && data.token && data.user) {
        this.stopQrPolling(false)
        auth.applyTokenSession({ token: data.token, user: data.user })
        this.$message.success(result.message || '扫码登录成功')
        this.$emit('success')
        this.loginMode = 'password'
        this.qrTicket = ''
        this.qrImage = ''
      }
    },
    stopQrPolling(cancelRemote) {
      if (this.qrPollTimer) {
        clearInterval(this.qrPollTimer)
        this.qrPollTimer = null
      }
      if (cancelRemote && this.qrTicket && this.qrStatus === 'pending') {
        auth.cancelQrLogin(this.qrTicket)
      }
    },
    goForgot() {
      this.stopQrPolling(true)
      this.loginMode = 'password'
      this.activeTab = 'forgot'
      this.resetStep = 1
    },
    goBackLogin() {
      this.activeTab = 'login'
      this.resetForgotForm()
    },
    resetForgotForm() {
      this.resetStep = 1
      this.forgotForm = {
        email: '',
        code: '',
        password: '',
        confirmPassword: ''
      }
      this.clearCodeTimer()
      this.$refs.forgotEmailForm && this.$refs.forgotEmailForm.resetFields()
      this.$refs.forgotResetForm && this.$refs.forgotResetForm.resetFields()
    },
    startCodeCountdown() {
      this.clearCodeTimer()
      this.codeCountdown = 60
      this.codeTimer = setInterval(() => {
        if (this.codeCountdown <= 1) {
          this.clearCodeTimer()
          this.codeCountdown = 0
        } else {
          this.codeCountdown -= 1
        }
      }, 1000)
    },
    clearCodeTimer() {
      if (this.codeTimer) {
        clearInterval(this.codeTimer)
        this.codeTimer = null
      }
    },
    startRegisterCodeCountdown() {
      this.clearRegisterCodeTimer()
      this.registerCodeCountdown = 60
      this.registerCodeTimer = setInterval(() => {
        if (this.registerCodeCountdown <= 1) {
          this.clearRegisterCodeTimer()
          this.registerCodeCountdown = 0
        } else {
          this.registerCodeCountdown -= 1
        }
      }, 1000)
    },
    clearRegisterCodeTimer() {
      if (this.registerCodeTimer) {
        clearInterval(this.registerCodeTimer)
        this.registerCodeTimer = null
      }
    },
    async handleSendRegisterCode() {
      const email = this.registerForm.email.trim()
      if (!email) {
        this.$message.warning('请先填写邮箱')
        return
      }
      const emailRule = this.registerRules.email
      let valid = true
      for (const rule of emailRule) {
        if (rule.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          this.$message.warning(rule.message)
          valid = false
          break
        }
      }
      if (!valid) return

      this.loading = true
      const result = await auth.sendRegisterCode(email)
      this.loading = false
      if (result.success) {
        this.$message.success(result.message)
        this.startRegisterCodeCountdown()
      } else {
        this.$message.error(result.message)
      }
    },
    async handleSendCode() {
      const formRef = this.resetStep === 1 ? 'forgotEmailForm' : 'forgotResetForm'

      const send = async () => {
        this.loading = true
        const result = await auth.sendResetCode(this.forgotForm.email.trim())
        this.loading = false

        if (result.success) {
          this.$message.success(result.message)
          this.resetStep = 2
          this.startCodeCountdown()
        } else {
          this.$message.error(result.message)
        }
      }

      if (this.resetStep === 1) {
        this.$refs[formRef].validate(valid => {
          if (valid) send()
        })
      } else if (this.forgotForm.email) {
        send()
      }
    },
    async handleResetPassword() {
      this.$refs.forgotResetForm.validate(async valid => {
        if (!valid) return
        this.loading = true
        const result = await auth.resetPassword({
          email: this.forgotForm.email.trim(),
          code: this.forgotForm.code.trim(),
          password: this.forgotForm.password
        })
        this.loading = false

        if (result.success) {
          this.$message.success(result.message)
          this.loginForm.account = this.forgotForm.email.trim()
          this.loginForm.password = ''
          this.resetForgotForm()
          this.activeTab = 'login'
        } else {
          this.$message.error(result.message)
        }
      })
    },
    async handleLogin() {
      this.$refs.loginForm.validate(async valid => {
        if (!valid) return
        this.loading = true
        const result = await auth.login(this.loginForm)
        this.loading = false

        if (result.success) {
          this.$message.success(result.message)
          this.loginForm = { account: '', password: '' }
          this.$emit('success')
        } else {
          this.$message.error(result.message)
        }
      })
    },
    async handleRegister() {
      this.$refs.registerForm.validate(async valid => {
        if (!valid) return
        this.loading = true
        const { username, email, password, code } = this.registerForm
        const result = await auth.register({ username, email, password, code })
        this.loading = false

        if (result.success) {
          this.$message.success(result.message)
          this.registerForm = {
            username: '',
            email: '',
            code: '',
            password: '',
            confirmPassword: ''
          }
          this.clearRegisterCodeTimer()
          this.registerCodeCountdown = 0
          this.$emit('success')
        } else {
          this.$message.error(result.message)
        }
      })
    },
    handleClose() {
      this.stopQrPolling(true)
      this.loginMode = 'password'
      this.$refs.loginForm && this.$refs.loginForm.resetFields()
      this.$refs.registerForm && this.$refs.registerForm.resetFields()
      this.clearRegisterCodeTimer()
      this.registerCodeCountdown = 0
      this.resetForgotForm()
    }
  }
}
</script>

<style lang="scss">
@import '@/assets/styles/variables.scss';

.auth-dialog-modern {
  border-radius: 20px !important;
  overflow: hidden;
  box-shadow: 0 24px 48px rgba(37, 99, 235, 0.18), 0 8px 16px rgba(0, 0, 0, 0.06) !important;

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 0;
    position: relative;
  }

  @media (max-width: 768px) {
    width: min(100%, 100vw - 24px) !important;
    max-width: 440px;
    margin-top: 4vh !important;
    border-radius: 16px !important;
  }
}
</style>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';

.dialog-close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.04);
  color: $text-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.08);
    color: $text-primary;
    transform: rotate(90deg);
  }

  i { font-size: 16px; }
}

.auth-layout {
  display: grid;
  grid-template-columns: 340px 1fr;
  min-height: 520px;
}

/* 左侧品牌面板 */
.auth-brand {
  position: relative;
  background: linear-gradient(145deg, #1d4ed8 0%, #2563eb 40%, #3b82f6 100%);
  padding: 48px 36px;
  display: flex;
  align-items: center;
  overflow: hidden;
}

.brand-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;

  .bg-circle {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.08);
  }

  .c1 {
    width: 200px;
    height: 200px;
    top: -60px;
    right: -40px;
  }

  .c2 {
    width: 120px;
    height: 120px;
    bottom: 40px;
    left: -30px;
  }

  .c3 {
    width: 80px;
    height: 80px;
    bottom: 120px;
    right: 30px;
    background: rgba(255, 255, 255, 0.12);
  }
}

.brand-content {
  position: relative;
  z-index: 1;
  color: #fff;

  h2 {
    font-size: 28px;
    font-weight: 700;
    margin-bottom: 10px;
    letter-spacing: 0.5px;
  }

  p {
    font-size: 14px;
    opacity: 0.85;
    line-height: 1.6;
    margin-bottom: 32px;
  }
}

.brand-logo {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 28px;

  i {
    font-size: 28px;
    color: #fff;
  }
}

.brand-features {
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    opacity: 0.9;
    margin-bottom: 14px;

    i {
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.2);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      flex-shrink: 0;
    }
  }
}

/* 右侧表单面板 */
.auth-form-panel {
  padding: 40px 44px 36px;
  display: flex;
  flex-direction: column;
  background: $bg-white;
}

.form-header {
  margin-bottom: 28px;
}

.tab-switch {
  position: relative;
  display: flex;
  background: $bg-gray;
  border-radius: 12px;
  padding: 4px;
}

.tab-btn {
  flex: 1;
  position: relative;
  z-index: 1;
  padding: 10px 0;
  border: none;
  background: transparent;
  font-size: 15px;
  font-weight: 500;
  color: $text-secondary;
  cursor: pointer;
  transition: color 0.3s;
  border-radius: 10px;

  &.active {
    color: $primary-color;
  }
}

.tab-indicator {
  position: absolute;
  top: 4px;
  left: 4px;
  width: calc(50% - 4px);
  height: calc(100% - 8px);
  background: $bg-white;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.1);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &.register {
    transform: translateX(100%);
  }
}

.form-body {
  flex: 1;
}

.login-mode-switch {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
  padding: 4px;
  background: $bg-gray;
  border-radius: 12px;
}

.mode-btn {
  flex: 1;
  height: 40px;
  border: none;
  border-radius: 10px;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: $text-secondary;
  cursor: pointer;
  transition: all 0.25s;

  &.active {
    background: $bg-white;
    color: $primary-color;
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.12);
  }
}

.qr-login-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 0 4px;
}

.qr-box {
  position: relative;
  width: 200px;
  height: 200px;
  border-radius: 16px;
  background: $bg-gray;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.qr-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.qr-empty {
  text-align: center;
  color: $text-muted;
  padding: 16px;

  i {
    font-size: 36px;
    display: block;
    margin-bottom: 8px;
  }

  p {
    font-size: 13px;
    line-height: 1.5;
    margin: 0;
  }
}

.qr-mask {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: $primary-color;
}

.qr-status-text {
  margin: 16px 0 4px;
  font-size: 13px;
  color: $text-secondary;
  text-align: center;
  line-height: 1.5;
  min-height: 40px;
}

.field-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: $text-primary;
  margin-bottom: 6px;
}

.field-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;

  .field-label {
    margin-bottom: 0;
  }
}

.forgot-link {
  font-size: 12px;
  color: $primary-color;
  font-weight: 500;

  &:hover {
    color: $primary-light;
    text-decoration: underline;
  }
}

.forgot-header {
  h3 {
    font-size: 20px;
    font-weight: 700;
    color: $text-primary;
    margin-bottom: 4px;
  }

  p {
    font-size: 13px;
    color: $text-muted;
  }
}

.back-login-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  padding: 0;
  margin-bottom: 12px;
  font-size: 13px;
  color: $text-muted;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: $primary-color;
  }
}

.reset-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: 24px;

  .step {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: 2px solid $border-color;
    color: $text-muted;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: center;
    background: $bg-white;
    transition: all 0.25s;

    &.active {
      border-color: $primary-color;
      color: $primary-color;
      background: $primary-bg;
    }

    &.done {
      border-color: $primary-color;
      background: $primary-color;
      color: #fff;
    }
  }

  .step-line {
    width: 48px;
    height: 2px;
    background: $border-color;
    transition: background 0.25s;

    &.active {
      background: $primary-color;
    }
  }
}

.code-input-row {
  display: flex;
  gap: 10px;

  .el-input {
    flex: 1;
  }
}

.code-btn {
  flex-shrink: 0;
  height: 44px;
  padding: 0 14px;
  border-radius: 10px;
  font-size: 12px;
  white-space: nowrap;
}

.form-body ::v-deep {
  .el-form-item {
    margin-bottom: 18px;
  }

  .el-input__inner {
    height: 44px;
    line-height: 44px;
    border-radius: 10px;
    border-color: $border-color;
    background: $bg-gray;
    transition: all 0.25s;

    &:focus {
      border-color: $primary-color;
      background: $bg-white;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    }
  }

  .el-input__prefix {
    left: 12px;
    color: $text-muted;
  }

  .el-input--prefix .el-input__inner {
    padding-left: 40px;
  }

  .el-form-item__error {
    padding-top: 4px;
    font-size: 12px;
  }
}

.submit-btn {
  width: 100%;
  height: 46px;
  margin-top: 8px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 1px;
  background: linear-gradient(135deg, $primary-color 0%, $primary-light 100%);
  border: none;
  transition: all 0.3s;

  i {
    margin-left: 6px;
    transition: transform 0.3s;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.35);

    i {
      transform: translateX(3px);
    }
  }

  &:active {
    transform: translateY(0);
  }
}

.form-footer {
  text-align: center;
  margin-top: 20px;
  font-size: 13px;
  color: $text-secondary;

  a {
    color: $primary-color;
    font-weight: 500;
    margin-left: 4px;

    &:hover {
      color: $primary-light;
      text-decoration: underline;
    }
  }
}

/* 表单切换动画 */
.form-slide-enter-active,
.form-slide-leave-active {
  transition: all 0.25s ease;
}

.form-slide-enter {
  opacity: 0;
  transform: translateX(16px);
}

.form-slide-leave-to {
  opacity: 0;
  transform: translateX(-16px);
}

@media (max-width: 768px) {
  .auth-layout {
    grid-template-columns: 1fr;
    min-height: auto;
  }

  .auth-brand {
    padding: 24px 22px 20px;
  }

  .brand-features {
    display: none;
  }

  .brand-content h2 {
    font-size: 20px;
  }

  .auth-form-panel {
    padding: 22px 18px 28px;
  }

  .dialog-close {
    top: 10px;
    right: 10px;
    background: rgba(255, 255, 255, 0.85);
  }
}
</style>
